export interface CognitoConfig {
  domain: string;
  clientId: string;
  redirectUri: string;
  scope: string;
  useRefreshToken: boolean;
  refreshLeewaySeconds: number;
}

function normalizeDomain(domain: string): string {
  return domain.replace(/\/$/, '');
}

// PKCE helper functions
export async function generatePKCEChallenge(): Promise<{ codeVerifier: string; codeChallenge: string }> {
  const codeVerifier = generateRandomString(128);
  const codeChallenge = await base64UrlEncode(await sha256(codeVerifier));
  return { codeVerifier, codeChallenge };
}

function generateRandomString(length: number): string {
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += charset.charAt(Math.floor(Math.random() * charset.length));
  }
  return result;
}

async function sha256(str: string): Promise<ArrayBuffer> {
  const encoder = new TextEncoder();
  const data = encoder.encode(str);
  return crypto.subtle.digest('SHA-256', data);
}

async function base64UrlEncode(buffer: ArrayBuffer): Promise<string> {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

const env = ((window as any).configuration?.enviroment ?? {}) as Record<string, string>;

const configuredDomain = env['CognitoDomain'] || 'https://mempicturesportal.auth.us-east-1.amazoncognito.com';

export const cognitoConfig: CognitoConfig = {
  domain: normalizeDomain(configuredDomain),
  clientId: env['CognitoClientId'] || 'your-cognito-app-client-id',
  redirectUri: env['CognitoRedirectUri'] || `${window.location.origin}/driver`,
  scope: env['CognitoScope'] || 'openid email phone offline_access',
  useRefreshToken: (env['CognitoUseRefreshToken'] || 'true').toLowerCase() === 'true',
  refreshLeewaySeconds: Number(env['CognitoRefreshLeewaySeconds'] || '60'),
};

export function getCognitoLoginUrl(config: CognitoConfig, codeChallenge?: string): string {
  const params = new URLSearchParams({
    client_id: config.clientId,
    response_type: 'code',
    scope: config.scope,
    redirect_uri: config.redirectUri,
  });

  if (codeChallenge) {
    params.append('code_challenge', codeChallenge);
    params.append('code_challenge_method', 'S256');
  }

  return `${config.domain}/login?${params.toString()}`;
}

export function getCognitoTokenUrl(config: CognitoConfig): string {
  return `${config.domain}/oauth2/token`;
}

export function getCognitoUserInfoUrl(config: CognitoConfig): string {
  return `${config.domain}/oauth2/userInfo`;
}

export function getCognitoLogoutUrl(config: CognitoConfig): string {
  const params = new URLSearchParams({
    client_id: config.clientId,
    logout_uri: config.redirectUri,
  });
  return `${config.domain}/logout?${params.toString()}`;
}
